import { Injectable } from '@angular/core';
import { Control, type ILaneConfig, type LaneConfig, type StoreControl, type TelemetryDataValues } from 'commons';
import { isValid, parseISO } from 'date-fns';
import { evaluate } from 'mathjs';
import { db } from '../../../app/db';
import { fillSeriesToResolutionWithNull } from '../helper/fillSeriesToResolutionWithNull';
import type { SeriesDataPoint } from './echarts-config.types';

/**
 * Service for extracting and transforming series data from telemetry sources.
 * Handles both time-based and index-based data retrieval and transformations.
 */
@Injectable({ providedIn: 'root' })
export class SeriesDataService {
  /**
   * Retrieves time-based series data for a control
   */
  async getTimeBasedSeriesData(control: StoreControl, laneConfig: LaneConfig | ILaneConfig): Promise<SeriesDataPoint[]> {
    const timeField = laneConfig.config.xAxisField?.label ?? 'datetime';
    const mappingKey = new Control(control).mappingString;

    let timeseries: TelemetryDataValues[] = [];
    try {
      timeseries = (await db.channelData.get(mappingKey))?.timeseries ?? [];
    } catch (error) {
      console.error('Error while fetching timeseries', error);
    }

    const controlWrapper = new Control(control);

    return fillSeriesToResolutionWithNull(
      timeseries
        .filter(this.hasField(timeField))
        .filter(this.hasField(control.label))
        .sort(this.sortByDateField(timeField))
        .reverse()
        .map((_entry, index) => this.createDataPoint(timeseries, timeField, controlWrapper, index)),
      control
    ) as Promise<SeriesDataPoint[]>;
  }

  /**
   * Retrieves index-based series data for a control
   */
  async getIndexBasedSeriesData(yControl: Control, laneConfig: LaneConfig | ILaneConfig): Promise<SeriesDataPoint[]> {
    const yTimeseries = (await db.channelData.get(yControl.mappingString))?.timeseries;

    if (!yTimeseries) {
      return [[0, 0, '']];
    }

    if (yControl.isOffline) {
      return yTimeseries.map((entry, index) => [index, entry[yControl.label] as number, yControl.unit ?? '']);
    }

    if (!laneConfig.config.xAxisField?.label) {
      return [[0, 0, '']];
    }

    const xControl = new Control(laneConfig.config.xAxisField);
    const xTimeseries = (await db.channelData.get(xControl.mappingString))?.timeseries;

    if (!xTimeseries) {
      return [];
    }

    const combined = yTimeseries.map((entry, index) => ({
      ...entry,
      ...xTimeseries[index],
    }));

    const sorted = combined.filter(this.hasFields([yControl.label, xControl.label])).sort(this.sortByField(xControl.label));

    return sorted.map((_, index) => this.createDataPoint(sorted, xControl, yControl, index));
  }

  /**
   * Creates a data point from telemetry
   */
  createDataPoint(timeseries: TelemetryDataValues[], xSource: string | Control, yControl: Control, index: number): SeriesDataPoint {
    if (!timeseries[index]) {
      return [0, 0, yControl.unit ?? ''];
    }

    const entry = timeseries[index];
    const xValue = this.extractXValue(entry, xSource);
    const yValue = this.extractYValue(entry, yControl);

    return [xValue, yValue, yControl.unit ?? ''];
  }

  /**
   * Extracts X-axis value from telemetry entry
   */
  private extractXValue(entry: TelemetryDataValues, xSource: string | Control): Date | number {
    if (typeof xSource === 'string') {
      const value = entry[xSource];
      if (xSource === 'datetime' && isValid(parseISO(String(value)))) {
        return new Date(entry.datetime);
      }
      return Number(value);
    }
    return Number(entry[xSource.label]);
  }

  /**
   * Extracts Y-axis value from telemetry entry
   */
  private extractYValue(entry: TelemetryDataValues, control: Control): number | null {
    if (control.transformation) {
      return this.calculateTransformation(control, entry);
    }
    return entry[control.label] as number | null;
  }

  /**
   * Evaluates control transformation expression
   */
  private calculateTransformation(control: Control, telemetry: TelemetryDataValues): number | null {
    try {
      return evaluate(String(control.transformation), {
        ...telemetry,
        $: telemetry[control.label],
      });
    } catch (error) {
      console.error('Transformation error:', error);
      return telemetry[control.label] as number | null;
    }
  }

  /**
   * Checks if field exists in telemetry entry
   */
  private readonly hasField =
    (field: string) =>
    (entry: TelemetryDataValues): boolean =>
      entry[field] !== null && entry[field] !== undefined;

  /**
   * Checks if multiple fields exist in telemetry entry
   */
  private readonly hasFields =
    (fields: string[]) =>
    (entry: TelemetryDataValues): boolean =>
      fields.every((field) => !!entry[field]);

  /**
   * Sorts telemetry entries by date field
   */
  private readonly sortByDateField =
    (field: string) =>
    (a: TelemetryDataValues, b: TelemetryDataValues): number =>
      new Date(a[field]).getTime() < new Date(b[field]).getTime() ? 1 : -1;

  /**
   * Sorts telemetry entries by numeric field
   */
  private readonly sortByField =
    <T>(field: keyof T) =>
    (a: T, b: T): number =>
      (a[field] as number) - (b[field] as number);
}
